import { ProjectService } from './../../shared/generated/backend/api/project.service';
import { Component, signal, OnInit, ChangeDetectionStrategy, EventEmitter, ViewChild, inject } from '@angular/core';
import { ContextService } from '../../services/context.service';
import { ProcessingState, ProjectDto } from '../../shared/generated/backend';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import {
  DxBulletModule,
  DxButtonModule,
  DxDataGridModule,
  DxDateRangeBoxModule,
  DxDropDownBoxModule,
  DxDropDownButtonModule,
  DxFormModule,
  DxMultiViewModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTabsModule,
  DxTemplateModule,
  DxTreeViewComponent,
  DxTreeViewModule,
  DxTagBoxModule,
} from 'devextreme-angular';
import { LoaderComponent } from '../../loader/loader.component';
import { RowDblClickEvent } from 'devextreme/ui/data_grid';
import { NewProjectFormComponent } from '../new-project-form/new-project-form.component';
import notify from 'devextreme/ui/notify';
import { HelperService } from '../../services/helper.service';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { UtilsService } from '../../services/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { firstValueFrom } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { ValueChangedEvent } from 'devextreme/ui/tag_box';
import { DateHelper } from '../../shared/util/date-helper';
import { addDays, format } from 'date-fns';
import {
  BadgeVariantComponent,
  Shape,
} from '../../shared/components/badge-variant/badge-variant/badge-variant.component';

interface State {
  id: number;
  text: string;
  state: ProcessingState;
}
@Component({
  selector: 'app-project-list',
  imports: [
    CommonModule,
    SharedModule,
    DxDataGridModule,
    DxTemplateModule,
    DxBulletModule,
    DxDateRangeBoxModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxTabsModule,
    DxPopupModule,
    DxFormModule,
    DxMultiViewModule,
    LoaderComponent,
    IconComponent,
    DxDropDownBoxModule,
    DxDropDownButtonModule,
    DxTreeViewModule,
    NewProjectFormComponent,
    DxTagBoxModule,
    BadgeVariantComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './project-list.component.html',
  styleUrl: './project-list.component.scss',
})
export class ProjectListComponent implements OnInit {
  @ViewChild(NewProjectFormComponent, { static: false })
  newProjectFormComp!: NewProjectFormComponent;
  _projects: Array<ProjectDto> = [];
  readonly projects = signal(new Array<ProjectDto>());
  readonly Shape = Shape;

  dateRange!: [Date, Date];
  searchText: string = '';
  readonly isLoading = signal(false);
  readonly popupVisible = signal(false);
  readonly resetPopupStateEmitter = new EventEmitter<void>();

  private contextService = inject(ContextService);
  private projectService = inject(ProjectService);
  private helperService = inject(HelperService);
  public utilService = inject(UtilsService);
  public translateService = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);

  @ViewChild(DxTreeViewComponent, { static: false }) treeView!: DxTreeViewComponent;
  readonly _states: Array<ProcessingState> = [
    'TransportationScheduling',
    'TransportationSchedulingDone',
    'Approved',
    'ProcessingByWarehouse',
    'FixedByWarehouse',
    'Rolling',
    'Completed',
    'WaitingToUnload',
    'Unloading',
  ];

  states: Array<State> = [];
  selectedStates: Array<number> = [];

  async ngOnInit() {
    this.dateRange = this.helperService.getValue<[Date, Date]>('filterDateRange_project') || [
      DateHelper.oneWeekBeforeNow(),
      DateHelper.oneWeekAfterNow(),
    ];
    this.states = await Promise.all(
      this._states.map(async (t, i) => {
        const text = await firstValueFrom(this.translateService.get('PROCESSING_STATE.' + t));
        return {
          id: i,
          state: t,
          text: text,
        };
      })
    );
    this.selectedStates =
      this.helperService.getValue<Array<number>>('filterState') ??
      this.states.filter(s => s.state != 'Completed').map(t => t.id);
    this.getProjects();
  }

  loadProject(event: RowDblClickEvent) {
    this.contextService.openContextView(`project/${event.data.ProjectNo}`);
  }

  async getProjects() {
    this.helperService.setValue<[Date, Date]>('filterDateRange_project', this.dateRange); // user previously set date range

    const fromDate = format(this.dateRange[0], 'yyyy-MM-dd');
    const toDate = format(addDays(this.dateRange[1], 1), 'yyyy-MM-dd');

    // console.log(`getProjects(${fromDate}, ${toDate})`);
    this.isLoading.set(true);
    this.projectService.getProjects(fromDate, toDate).subscribe({
      next: returnValue => {
        this._projects = returnValue;
        this.refreshProjects();
        // console.log('getProjects', returnValue);
      },
      error: err => {
        notify({ message: this.translateService.instant('ERROR.GENERAL'), shading: false }, 'error', 3000);
        console.error(err);
        this.isLoading.set(false);
      },
      complete: () => {
        this.isLoading.set(false);
        // await the rendering of the grid in order for the search text to be set
        new Promise(() =>
          setTimeout(() => {
            this.searchText = this.helperService.getValue<string>('filterSearchText_project') || '';
            this.cdRef.detectChanges();
          }, 500)
        );
      },
    });
  }

  refreshProjects() {
    let projects = this._projects;

    const states = this.states.filter(t => this.selectedStates.includes(t.id)).map(t => t.state);
    projects = projects.filter(t => states.includes(t.ProcessingState!));

    this.projects.set(projects);
  }

  showPopup() {
    this.popupVisible.set(true);
    this.resetPopupStateEmitter.next();
  }

  onTreeViewReady(e: DxTreeViewTypes.ContentReadyEvent) {
    this.updateSelection(e.component);
  }

  onTreeViewSelectionChanged(e: DxTreeViewTypes.ItemSelectionChangedEvent) {
    this.selectedStates = e.component.getSelectedNodeKeys();
  }

  onDropDownBoxValueChanged() {
    this.helperService.setValue<Array<number>>('filterState', this.selectedStates); // user previously set date range
    this.updateSelection(this.treeView?.instance);
  }

  updateSelection(treeView: DxTreeViewComponent['instance']) {
    if (!treeView) return;

    if (!this.selectedStates) {
      treeView.unselectAll();
    }

    this.selectedStates?.forEach(value => {
      treeView.selectItem(value);
    });

    this.refreshProjects();
  }

  handlePopupHidden() {
    this.popupVisible.set(false);
    this.newProjectFormComp.resetForm();
    this.cdRef.detectChanges();
  }

  onTagBoxValueChanged(e: ValueChangedEvent) {
    this.selectedStates = e.value;
    this.helperService.setValue<Array<number>>('filterState', this.selectedStates);
    this.getProjects();
  }

  persistSearchtext(value: string) {
    this.helperService.setValue<string>('filterSearchText_project', value);
  }
}
